import React from 'react';
import { TableRow, TableCell } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  IdCard, 
  Eye,
  Mail, 
  Lock, 
  Users 
} from 'lucide-react';
import { UserTableRowProps, UserRole } from '../types';

const UserTableRow: React.FC<UserTableRowProps> = ({
  user,
  onEditRoles,
  onEditProfile,
  onChangePassword,
  onViewPassword,
  onNotifyCredentials,
  loadingActions,
  canViewPasswords,
  canManageRoles,
}) => {
  const getUserRoles = (): string[] => {
    if (user.roles && user.roles.length > 0) {
      return user.roles.map(role => {
        if (typeof role === 'object' && (role as UserRole).name) {
          return (role as UserRole).name;
        }
        if (typeof role === 'string') {
          return role;
        }
        return '';
      }).filter(Boolean);
    }
    return [];
  };

  const formatRoleLabel = (role: string): string => {
    const roleLabels: Record<string, string> = {
      'super_admin': 'Super Admin',
      'tramitadora': 'Tramitadora',
      'contador': 'Contador',
      'escribano_externo': 'Escribano Externo',
      // Legacy contextual roles - should not be assigned directly to users
      'vendedor': 'Vendedor (Contextual)',
      'comprador': 'Comprador (Contextual)',
    };
    return roleLabels[role] || role;
  };

  const getRoleBadgeVariant = (role: string): 'default' | 'secondary' | 'outline' | 'destructive' => {
    switch (role) {
      case 'super_admin':
      case 'tramitadora':
        return 'default';
      case 'contador':
      case 'escribano_externo':
        return 'secondary';
      case 'vendedor':
      case 'comprador':
        return 'destructive'; // Changed to destructive to indicate these shouldn't be direct user roles
      default:
        return 'destructive';
    }
  };

  const userRoles = getUserRoles();
  const isLoadingRoles = loadingActions[`roles-${user.id}`];
  const isLoadingProfile = loadingActions[`profile-${user.id}`];
  const isLoadingPassword = loadingActions[`password-${user.id}`];
  const isLoadingViewPassword = loadingActions[`view-password-${user.id}`];
  const isLoadingNotify = loadingActions[`notify-${user.id}`];

  return (
    <TableRow>
      <TableCell>
        <div className="max-w-[16rem] truncate" title={user.email || undefined}>
          {user.email || 'Sin email'}
        </div>
      </TableCell>
      <TableCell>
        {user.name || 'Sin nombre'}
      </TableCell>
      <TableCell>
        {user.profile?.dni || 'Sin DNI'}
      </TableCell>
      <TableCell>
        {user.profile?.telefono || 'Sin teléfono'}
      </TableCell>
      <TableCell className="min-w-[10rem] whitespace-normal">
        <div className="flex flex-wrap gap-1">
          {userRoles.length > 0 ? (
            userRoles.map(role => (
              <Badge 
                key={role} 
                variant={getRoleBadgeVariant(role)}
                className="text-xs"
              >
                {formatRoleLabel(role)}
              </Badge>
            ))
          ) : (
            <Badge variant="destructive" className="text-xs">
              Sin roles
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell>
        <TooltipProvider>
          <div className="flex gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  loading={isLoadingProfile}
                  onClick={() => onEditProfile(user)}
                  className="rounded-full w-8 h-8 p-0"
                >
                  <IdCard className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Editar datos personales</p>
              </TooltipContent>
            </Tooltip>

            {canManageRoles && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    loading={isLoadingRoles}
                    onClick={() => onEditRoles(user)}
                    aria-label={`Gestionar roles de ${user.name}`}
                    className="rounded-full w-8 h-8 p-0"
                  >
                    <Users className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Gestionar roles</p>
                </TooltipContent>
              </Tooltip>
            )}

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  loading={isLoadingPassword}
                  onClick={() => onChangePassword(user)}
                  className="rounded-full w-8 h-8 p-0"
                >
                  <Lock className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Cambiar contraseña</p>
              </TooltipContent>
            </Tooltip>

            {canViewPasswords && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    loading={isLoadingViewPassword}
                    onClick={() => onViewPassword(user)}
                    aria-label={`Ver contraseña de ${user.name}`}
                    className="rounded-full w-8 h-8 p-0"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Ver contraseña</p>
                </TooltipContent>
              </Tooltip>
            )}

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  loading={isLoadingNotify}
                  onClick={() => onNotifyCredentials(user)}
                  className="rounded-full w-8 h-8 p-0"
                >
                  <Mail className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Enviar credenciales</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </TableCell>
    </TableRow>
  );
};

export default UserTableRow;
